import React from 'react';
import {GlobalContext} from "../../GlobalContext";
import {Card, FormControl, Table} from "react-bootstrap";
import moment from "moment-timezone";
import {Link} from "react-router-dom";
import {TIMEZONE} from "../../utils/Dates";
import {SpinnerWrapper} from "../common/SpinnerWrapper";

function formatPatientName(patient) {
    if (!patient) return '';
    return `${patient.lastName}, ${patient.firstName}`;
}

function formatReferringProvider(referringProvider) {
    if (!referringProvider) return '';
    if (referringProvider.practiceName) return referringProvider.practiceName;
    return `${referringProvider.lastName}, ${referringProvider.firstName}`;
}

function formatTimestamp(timestamp) {
    return timestamp ? moment(timestamp).tz(TIMEZONE).format('M/D/YYYY h:mm A') : '';
}

function formatReferralCategory(category) {
    if (category === 'LIEN') return 'Lien';
    if (category === 'WORKERS_COMP') return 'WC';
    return '';
}

function formatUploadRow(upload) {
    const parts = [formatPatientName(upload.patient), formatReferringProvider(upload.referringProvider), formatTimestamp(upload.timestamp)];
    const categoryLabel = formatReferralCategory(upload.referralCategory);
    if (categoryLabel) {
        parts.unshift(`[${categoryLabel}]`);
    }
    return parts.filter(Boolean).join(' — ');
}

function parseAuditTags(tags) {
    if (!tags) return [];
    return String(tags).split(',').map(tag => tag.trim()).filter(Boolean);
}

export class PendingReferralUploadsList extends React.Component {
    static contextType = GlobalContext;

    constructor(props) {
        super(props);
        this.state = {
            listTitle: "Loading...",
            uploads: [],
            filterText: "",
            searching: false
        };
    }

    refreshTimer = 0;
    filterDebounceTimer = 0;
    searchRequestId = 0;

    componentDidMount() {
        this.loadUploads();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.lastForceRefresh !== this.props.lastForceRefresh) {
            if (this.state.filterText.trim()) {
                this.searchUploads(this.state.filterText);
            } else {
                this.loadUploads();
            }
        }
    }

    componentWillUnmount() {
        if (this.refreshTimer) {
            window.clearTimeout(this.refreshTimer);
        }
        if (this.filterDebounceTimer) {
            window.clearTimeout(this.filterDebounceTimer);
        }
    }

    clearRefreshTimer = () => {
        if (this.refreshTimer) {
            window.clearTimeout(this.refreshTimer);
            this.refreshTimer = 0;
        }
    };

    loadUploads = () => {
        this.clearRefreshTimer();
        this.setState({searching: false});

        if (this.context.authenticated) {
            this.context.apiRequest("GET", "/referrals/pendingUploads", null, true)
                .then(d => {
                    if (this.state.filterText.trim()) return;
                    if (d && d.data) {
                        this.setState({uploads: d.data, listTitle: "Incoming Referrals Awaiting Review", searching: false});
                    }
                    this.refreshTimer = window.setTimeout(this.loadUploads, 60000);
                });
        }
    };

    searchUploads = (filterText) => {
        const q = filterText.trim();
        if (!q) {
            this.loadUploads();
            return;
        }

        this.clearRefreshTimer();
        if (!this.context.authenticated) return;

        const requestId = ++this.searchRequestId;
        this.setState({searching: true});
        this.context.apiRequest("GET", `/referrals/pendingUploads/search?q=${encodeURIComponent(q)}`, null, true)
            .then(d => {
                if (requestId !== this.searchRequestId) return;
                if (d && d.data) {
                    this.setState({uploads: d.data, listTitle: "Incoming Referrals Awaiting Review", searching: false});
                } else {
                    this.setState({searching: false});
                }
            })
            .catch(() => {
                if (requestId !== this.searchRequestId) return;
                this.setState({searching: false});
            });
    };

    onFilterChange = (e) => {
        const filterText = e.target.value;
        this.setState({filterText});

        if (this.filterDebounceTimer) {
            window.clearTimeout(this.filterDebounceTimer);
        }
        this.filterDebounceTimer = window.setTimeout(() => {
            this.searchUploads(filterText);
        }, 300);
    };

    render() {
        return (
            <Card className={this.props.className} style={{display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                <Card.Header>{this.state.listTitle}</Card.Header>
                <Card.Body style={{display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden'}}>
                    <FormControl
                        type={"search"}
                        size={"sm"}
                        placeholder={"Filter..."}
                        className={"mb-2"}
                        value={this.state.filterText}
                        onChange={this.onFilterChange}
                    />
                    <div style={{overflowY: 'auto', flex: 1, minHeight: 0}}>
                        <SpinnerWrapper visible={this.state.searching}>
                            <Table size={"sm"} striped>
                                <tbody>
                                {
                                    this.state.uploads.map(upload => {
                                        const tags = parseAuditTags(upload.tags);
                                        return (
                                            <tr key={upload.id}>
                                                <td className={"small clearfix"}>
                                                    {tags.length > 0 && (
                                                        <span className="audit-tag-pills">
                                                            {tags.map(tag => (
                                                                <span key={tag} className={tag}>{tag}</span>
                                                            ))}
                                                        </span>
                                                    )}
                                                    <Link to={`/referrals/upload?jobId=${upload.jobId}`} className="text-dark">
                                                        {formatUploadRow(upload)}
                                                    </Link>
                                                </td>
                                            </tr>
                                        );
                                    })
                                }
                                </tbody>
                            </Table>
                        </SpinnerWrapper>
                    </div>
                </Card.Body>
            </Card>
        );
    }
}
